import { defineComponent, h, computed } from 'vue';
import { QIcon } from 'quasar';
import SchemaProperties2 from '../form2/SchemaProperties.jsx';
import Pg2Row from '../form2/Pg2Row.jsx';
import Pg2FullRow from '../form2/Pg2FullRow.jsx';
import { getPropertyGrid2ElementType } from '../form2/elementTypes.js';
import { FLOW_CONTEXT_INJECT_KEY } from '../schemaform/flowDesignerContext.js';

import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';

const SPLITTER_LS_PREFIX = 'propertyGrid2.splitter.';
const SPLIT_MIN_PCT = 12;
const SPLIT_MAX_PCT = 88;

function toggleVisible(control, key, child) {
    const default_value = child <= 0 ? 'true' : 'false';
    const value = control.visible_keys[key] || localStorage.getItem(key);

    if (!value) {
        const new_value = default_value === 'true' ? 'false' : 'true';
        localStorage.setItem(key, new_value);
        control.visible_keys[key] = new_value;
        utils.log('Saved ' + key + ' as ' + new_value);
    } else {
        const new_value = value === 'true' ? 'false' : 'true';
        if (new_value === default_value) {
            localStorage.removeItem(key);
            delete control.visible_keys[key];
            utils.log('Deleted ' + key);
        } else {
            localStorage.setItem(key, new_value);
            control.visible_keys[key] = new_value;
            utils.log('Saved ' + key + ' as ' + new_value);
        }
    }
}

function getVisibility(control, key, child) {
    const default_value = child <= 0 ? 'true' : 'false';
    let value = control.visible_keys[key];
    if (!value) {
        value = localStorage.getItem(key);
        if (value) control.visible_keys[key] = value;
    }

    if (!value) return default_value === 'true';
    return value === 'true';
}

function splitterStorageKey(schema) {
    const id = schema && schema.Id != null ? String(schema.Id) : 'default';
    return SPLITTER_LS_PREFIX + id;
}

function getFormElements(h, control, schema, form_model, element_model, elements, root, child, path) {
    let cmodel = element_model;
    if (cmodel === undefined || cmodel === null) return;

    if (!cmodel.$objectId) cmodel['$objectId'] = utils.generateUUID();

    for (let key in schema.properties) {
        if (key.substr(0, 1) == '$' && key !== '$ref') continue;

        const uniquerowkey = cmodel.$objectId + '_' + key + '_' + child;

        const value = element_model[key];
        let element = utils.schema.resolve_Of(schema.properties[key]);

        let Tag = getPropertyGrid2ElementType(element);

        if (!Tag && element.type == 'object') {
            if (!cmodel[key]) cmodel[key] = {};

            if (element.$$condition && typeof element.$$condition === 'function')
                try {
                    const schema_context = utils.schema.getConditionContext(control, {
                        FormModel: control.cmodel,
                        ParentModel: cmodel,
                        IsAdvanced: control.IsAdvanced,
                        FlowContext: control.flowContext || control.FlowContext,
                    });

                    if (!element.$$condition(schema_context)) continue;
                } catch (e) {
                    utils.warn('Schema condition expression ' + element.condition + ' failed to evaluate: ' + e);
                }

            const visible_key = [...path, key].join('.');
            const vis = getVisibility(control, visible_key, child);
            const icon = vis ? (
                <i class="mdi mdi-minus-box-outline"></i>
            ) : (
                <i class="mdi mdi-plus-box"></i>
            );
            let err;
            if (element.formatData?.error)
                err = utils.tt(element.formatData.error, <QIcon name="mdi-alert" size="xs" color="negative" />);

            elements.push(
                <Pg2FullRow key={uniquerowkey + '_hdr'}>
                    <div
                        style={{ backgroundColor: 'silver', cursor: control.readonly ? 'default' : 'pointer' }}
                        onClick={() => {
                            if (!control.readonly)
                                toggleVisible(control, visible_key, child);
                        }}
                    >
                        <span style={{ marginLeft: child * 15 + 'px' }}>
                            <span class="treeview-expand-collapse">{icon}</span> {element.title || key}
                            {err}
                        </span>
                    </div>
                </Pg2FullRow>,
            );
            if (vis) {
                if (element.format === 'SchemaProperty')
                    elements.push(
                        <Pg2FullRow key={uniquerowkey + '_sch'}>
                            <SchemaProperties2
                                name={element.title || key}
                                propertygrid={true}
                                schemakey={key}
                                schema={element}
                                root={root}
                                cmodel={cmodel}
                                form_model={control.cmodel}
                                readonly={control.readonly || false}
                                canNavigate={control.canNavigateTo || control.canNavigate}
                                navigate={(ctrl, prop, value) =>
                                    control.internalNavigateTo(element, cmodel, key, ctrl, prop, value)
                                }
                            />
                        </Pg2FullRow>,
                    );
                else
                    getFormElements(h, control, element, form_model, cmodel[key], elements, root, child + 1, [
                        ...path,
                        key,
                    ]);
            }
            continue;
        }

        if (!Tag) {
            utils.log('PropertyGrid2: skipping property (no component) ' + key);
            continue;
        }

        elements.push(
            <Tag
                key={uniquerowkey}
                name={element.title || key}
                propertygrid={true}
                schemakey={key}
                schema={element}
                root={root}
                cmodel={cmodel}
                form_model={form_model}
                child={child}
                path={path}
                readonly={control.readonly || element.readonly || false}
                uniquerowkey={uniquerowkey}
                canNavigate={control.canNavigateTo || control.canNavigate}
                navigateTo={(ctrl, prop, value) => control.internalNavigateTo(element, cmodel, key, ctrl, prop, value)}
                navigateToModel={control.navigateToModel}
            />,
        );
    }
}

export default defineComponent({
    name: 'property-grid-2',
    components: {
        QIcon,
        SchemaProperties2,
        Pg2Row,
        Pg2FullRow,
    },
    inject: {
        pg2Nested: {
            from: 'pg2Nested',
            default: false,
        },
        _ancestorFlowContext: {
            from: FLOW_CONTEXT_INJECT_KEY,
            default: null,
        },
    },
    data: () => ({
        current_model: null,
        breadcrumb: [],
        visible_keys: {},
        labelPct: 50,
        _splitMove: null,
    }),
    created() {
        this.current_model = this.cmodel;

        let p = this.$parent;
        while (p && !('$on' in p)) p = p.$parent;
        if (p) p.$on('reset-breadcrumb', this.resetBreadcrumb);

        const schema = this.schema && utils.schema.resolve_Of(this.schema);
        const raw = localStorage.getItem(splitterStorageKey(schema));
        if (raw != null) {
            const n = parseFloat(raw);
            if (!Number.isNaN(n)) this.labelPct = Math.min(SPLIT_MAX_PCT, Math.max(SPLIT_MIN_PCT, n));
        }
    },
    unmounted() {
        this.teardownSplitDrag();
        let p = this.$parent;
        while (p && !('$off' in p)) p = p.$parent;
        if (p) p.$off('reset-breadcrumb', this.resetBreadcrumb);
    },
    props: {
        name: '',
        schemakey: null,
        root: null,
        schema: null,
        form: null,
        cmodel: null,
        child: 0,
        readonly: false,
        extra: null,
        navigateToModel: null,
        navigateTo: null,
        canNavigate: false,
        /** When true, schema fields with condition 'IsAdvanced' are shown (FlowV2 Advanced grid). */
        IsAdvanced: {
            type: Boolean,
            default: true,
        },
        /** Optional FlowV2 designer context for schema conditions / LookupURL. */
        flowContext: {
            type: Object,
            default: null,
        },
    },
    computed: {
        ...utils.forms.computed,
        canNavigateTo: function () {
            return this.navigateToModel ? true : false;
        },
        /** Prefer prop; fall back to ancestor provide (FlowContextProvider). */
        resolvedFlowContext() {
            if (this.flowContext)
                return this.flowContext;
            const anc = this._ancestorFlowContext;
            if (anc && typeof anc === 'object' && 'value' in anc && (anc.__v_isRef || Object.prototype.hasOwnProperty.call(anc, 'value')))
                return anc.value;
            return anc;
        },
    },
    provide() {
        return {
            pg2Nested: true,
            /** Pg2Row under this grid uses this for gutter drag (see Form2Array: no provide, so rows inherit here; nested PropertyGrid2 overrides for its subtree). */
            pg2SplitPointerDown: (e, gutterEl) => this.onSplitPointerDown(e, gutterEl),
            [FLOW_CONTEXT_INJECT_KEY]: computed(() => this.resolvedFlowContext),
        };
    },
    methods: {
        ...methods,
        textChanged(prop, cmodel, value) {
            try {
                const obj = JSON.parse(value);
                cmodel[prop] = obj;
            } catch (e) {
                utils.warn('Property grid failed to convert value to object: ' + e);
            }
        },
        internalNavigateTo(schema, cmodel, key, ctrl, prop, value) {
            if (this.navigateTo) this.navigateTo(ctrl, prop, value);
            else if (this.navigateToModel) {
                this.breadcrumb.push({ cmodel: this.cmodel, prevmodel: cmodel });
                if ((value === null || typeof value === 'undefined') && prop in cmodel) this.navigateToModel(cmodel[prop]);
                else this.navigateToModel(value);
            }
        },
        breadcrumbBack(index) {
            if (this.navigateToModel) {
                const newmodel = this.breadcrumb[index].cmodel;
                if (index === 0) this.breadcrumb = [];
                else this.breadcrumb = this.breadcrumb.slice(0, index);

                this.navigateToModel(newmodel);
            }
        },
        resetBreadcrumb() {
            this.breadcrumb = [];
        },
        persistSplitterPct() {
            try {
                const schema = this.schema && utils.schema.resolve_Of(this.schema);
                localStorage.setItem(splitterStorageKey(schema), String(this.labelPct));
            } catch (_) {
                /* ignore */
            }
        },
        teardownSplitDrag() {
            if (!this._splitMove) return;
            const { move, up, captureTarget, pointerId } = this._splitMove;
            if (captureTarget && typeof captureTarget.releasePointerCapture === 'function' && pointerId != null) {
                try {
                    if (captureTarget.hasPointerCapture?.(pointerId))
                        captureTarget.releasePointerCapture(pointerId);
                } catch (_) {
                    /* ignore */
                }
            }
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('pointercancel', up);
            this._splitMove = null;
        },
        blockReadonlyPointer(e) {
            e.preventDefault();
            e.stopPropagation();
        },
        /** gutterEl passed from Pg2Row so capture target is correct regardless of event.currentTarget */
        onSplitPointerDown(e, gutterEl) {
            if (this.readonly)
                return;
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            const root = this.$refs.pg2Root;
            if (!root) return;

            const target = gutterEl || e.currentTarget;
            const pointerId = e.pointerId;
            if (target && typeof target.setPointerCapture === 'function' && pointerId != null) {
                try {
                    target.setPointerCapture(pointerId);
                } catch (_) {
                    /* ignore */
                }
            }

            const move = (ev) => {
                const rect = root.getBoundingClientRect();
                if (rect.width <= 0) return;
                let pct = ((ev.clientX - rect.left) / rect.width) * 100;
                pct = Math.min(SPLIT_MAX_PCT, Math.max(SPLIT_MIN_PCT, pct));
                this.labelPct = pct;
            };
            const up = () => {
                this.teardownSplitDrag();
                this.persistSplitterPct();
            };
            this._splitMove = { move, up, captureTarget: target, pointerId };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            window.addEventListener('pointercancel', up);
            move(e);
        },
    },
    render() {
        let schema = this.schema;
        let elements = [];
        if (!schema) return null;

        try {
            schema = utils.schema.resolve_Of(schema);

            if (schema.type !== 'object') return <div>Schema must be an object</div>;

            let cmodel = this.cmodel;
            if (this.schemakey !== null && !(typeof this.schemakey === 'undefined')) cmodel = this.cmodel[this.schemakey];

            if (this.breadcrumb && this.breadcrumb.length > 0) {
                const crumbs = [];
                for (let i = 0; i < this.breadcrumb.length; i++) {
                    const bc = this.breadcrumb[i];
                    let name;
                    if (bc.prevmodel && bc.prevmodel.title) name = bc.prevmodel.title;
                    else if (bc.prevmodel && bc.prevmodel.Name) name = bc.prevmodel.Name;
                    else name = bc.cmodel.$typeSchema;

                    if (name) {
                        name = name.split('/');
                        name = name[name.length - 1];
                        if (name.includes('_')) {
                            name = name.split('_');
                            name = name[name.length - 1];
                        }
                    } else name = 'Back';

                    const crumb_style = {
                        margin: '3px',
                        cursor: 'pointer',
                        color: 'white',
                        backgroundColor: 'gray',
                        fontSize: 'small',
                        fontFamily: 'Arial',
                        padding: '2px',
                        borderRadius: '2px',
                    };

                    crumbs.push(
                        <span
                            style={crumb_style}
                            title="Back to Parent"
                            onClick={() => this.breadcrumbBack(i)}
                        >
                            <i class="mdi mdi-menu-left"></i> {name}
                        </span>,
                    );
                }
                elements.push(
                    <Pg2FullRow key="breadcrumb">
                        <div class="c-PropertyGrid2__breadcrumb">{crumbs}</div>
                    </Pg2FullRow>,
                );
            }

            getFormElements(h, this, schema, this.FormModel, cmodel, elements, this.root, this.child, [schema.Id]);

            /** Nested instance (object subtree): inject `pg2Nested` — suppress inner scroll + flex shrink (see App.vue). */
            const nested = !!this.pg2Nested;
            const readonly = !!this.readonly;

            /** Same as PropertyGrid v1 `table` + fit-content: natural height; only ancestor containers scroll. */
            const rootStyle = {
                '--pg2-label': this.labelPct + '%',
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                overflowY: 'visible',
                fontSize: '14px',
                ...(nested ? {} : { height: 'fit-content' }),
            };

            return (
                <div
                    ref="pg2Root"
                    class={{
                        'c-PropertyGrid2': true,
                        'c-PropertyGrid2--nested': nested,
                        'c-PropertyGrid2--readonly': readonly,
                        'designer-container': readonly,
                    }}
                    style={rootStyle}
                >
                    <div class="c-PropertyGrid2__scroll">{elements}</div>
                    {readonly ? (
                        <div
                            class="designer-container-hovering c-PropertyGrid2__readonlyOverlay"
                            title="Property editing is disabled"
                            onPointerDown={this.blockReadonlyPointer}
                            onMouseDown={this.blockReadonlyPointer}
                            onClick={this.blockReadonlyPointer}
                            onDblClick={this.blockReadonlyPointer}
                        />
                    ) : null}
                </div>
            );
        } catch (err) {
            console.error('Exception in PropertyGrid2', err);
            return <div>Exception in PropertyGrid2: {err.message}</div>;
        }
    },
});
